/**
 * Per-service FAQ content, shared between the rendered FAQ section on each
 * service page and the FAQPage JSON-LD emitted in that route's head().
 *
 * Keyed by service slug (the path segment under /services/).
 */
export type Faq = { q: string; a: string };

/** Builds a schema.org FAQPage node from a list of FAQs. */
export function faqPageSchema(faqs: Faq[], url?: string) {
  return {
    "@type": "FAQPage",
    ...(url ? { url } : {}),
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

export const serviceFaqs: Record<string, Faq[]> = {
  "dolby-atmos-home-theater": [
    {
      q: "How many speakers do I need for a Dolby Atmos home theater?",
      a: "The right layout depends on room size, seating, and construction. A 5.1.2 system uses five ear-level speakers, one subwoofer, and two overhead channels; larger rooms often benefit from 7.1.4 or more. We model the room and listening positions before recommending a layout.",
    },
    {
      q: "Can Dolby Atmos be installed in an existing Denver home?",
      a: "Yes. We regularly retrofit overhead and surround speakers into finished homes using attic, basement, soffit, or carefully planned wall access. If a direct wire path is not practical, we explain the tradeoffs and design the cleanest reliable alternative.",
    },
    {
      q: "Are in-ceiling speakers required for Dolby Atmos?",
      a: "In-ceiling speakers usually create the most convincing overhead effect, but angled on-ceiling or height speakers can be the better option for concrete ceilings, unusual framing, or rooms where cutting the ceiling is not appropriate. Up-firing modules are a last-choice compromise when direct speakers are not possible.",
    },
    {
      q: "Can you upgrade my current surround system to Dolby Atmos?",
      a: "Often, yes. We assess your receiver or processor, amplifier channels, existing speakers, wiring paths, and room layout. Some systems only need overhead channels and calibration; others need electronics with more processing and amplification capacity.",
    },
    {
      q: "Why does professional Dolby Atmos calibration matter?",
      a: "Speaker placement creates the geometry, but calibration makes the channels work together. We set levels, delays, crossovers, bass management, and room correction from the actual seating positions so dialogue stays anchored and effects move naturally through the room.",
    },
    {
      q: "Where does Apex AV install Dolby Atmos systems?",
      a: "We design, install, and calibrate Dolby Atmos home theaters throughout Denver and the Front Range, including Cherry Creek, Highlands Ranch, Littleton, Centennial, Parker, Castle Rock, Castle Pines, Lakewood, Golden, and Boulder.",
    },
  ],
  "hidden-audio": [
    {
      q: "How much do invisible speakers cost in Denver?",
      a: "Invisible in-wall speakers typically run $1,200–$2,400 per pair installed, including the plaster-in work and amplification channel. A whole-home invisible audio system across four to six zones usually lands between $9,000 and $30,000. Every Apex AV quote is fixed-bid before we open a wall.",
    },
    {
      q: "Do invisible speakers actually sound as good as regular in-wall speakers?",
      a: "Modern invisible drivers are genuinely close. In music and background-listening rooms most people can't tell the difference. For a dedicated theater or a critical listening room we still recommend conventional in-wall or on-wall speakers for the front stage, and use invisibles for surrounds and adjacent spaces.",
    },
    {
      q: "Can invisible speakers be installed in an existing Denver home?",
      a: "Yes. We cut a clean access opening, mount the panel to the studs, then skim, texture, and paint so the wall matches. Retrofit work is a large part of what we do across Cherry Creek, Hilltop, Wash Park, and Highlands Ranch.",
    },
    {
      q: "Can you paint or wallpaper over invisible speakers?",
      a: "That's the point. Once the panel is skimmed flush you can paint, texture, wallpaper, or apply Venetian plaster over it. There is no grille, no bezel, and no visible seam.",
    },
    {
      q: "Can invisible speakers go in a ceiling or outdoors?",
      a: "Both. We install invisible ceiling panels in bathrooms, hallways, and primary suites, and weather-rated invisible speakers under soffits and covered patios for Denver outdoor living spaces.",
    },
  ],
  "whole-home-audio": [
    {
      q: "How much does whole-home audio cost in Denver?",
      a: "A three-zone system with quality in-ceiling speakers and streaming amplification typically starts around $4,500 installed. Most Denver whole-home audio projects run $8,000–$35,000 depending on zone count, speaker grade, and whether the home is prewired.",
    },
    {
      q: "How many speakers do I need per room?",
      a: "Two speakers per room is the standard for even coverage and stereo imaging. Small bathrooms, closets, and hallways do fine with a single stereo-input speaker. Great rooms over about 400 square feet usually get four, and we add a subwoofer in main living and entertaining spaces.",
    },
    {
      q: "Can you install multi-room audio in an existing home without tearing up walls?",
      a: "Yes. Retrofit cable fishing is a specialty of ours — we pull speaker wire through existing walls and ceilings with minimal, patchable access points. Where a wire run genuinely isn't possible we use a wireless zone amp so the room still joins the system.",
    },
    {
      q: "Which music services work with your systems?",
      a: "All the major ones — Spotify, Apple Music, Amazon Music, Tidal, Qobuz, Pandora, SiriusXM, plus AirPlay 2, Bluetooth, and internet radio. You stream from the apps you already use; there is no proprietary app to learn.",
    },
    {
      q: "Can different rooms play different music at the same time?",
      a: "Yes. Every zone is independent, so the kitchen can run jazz while the gym runs a workout playlist and the patio runs the game. You can also group any combination of zones for parties.",
    },
    {
      q: "Do you install outdoor speakers too?",
      a: "We do — patio, pergola, pool, and landscape audio across the Denver Metro, all tied into the same system and app as the indoor zones.",
    },
  ],
  networking: [
    {
      q: "How much does a home network installation cost in Denver?",
      a: "A managed network with a router, switch, and two or three Wi-Fi access points typically runs $2,500–$6,000 installed. Larger Denver homes with structured Cat6, a full rack, and exterior coverage generally land between $6,000 and $20,000.",
    },
    {
      q: "Why is my Wi-Fi slow even with fast internet?",
      a: "Almost always coverage and channel congestion rather than bandwidth. A single consumer router can't cover a 4,000+ square foot Denver home with plaster or brick interior walls. Properly placed, hardwired access points on a managed network fix it far more reliably than a faster internet plan.",
    },
    {
      q: "Mesh Wi-Fi or hardwired access points — which is better?",
      a: "Hardwired access points, every time, when we can run cable. Mesh nodes repeat wirelessly and lose roughly half the throughput per hop. We use mesh only in retrofits where a wire genuinely can't be pulled.",
    },
    {
      q: "Do you run Cat6 and fiber in existing homes?",
      a: "Yes. We fish Cat6 and fiber through finished walls and ceilings daily, patch and paint behind ourselves, and terminate everything in a labeled rack or enclosure.",
    },
    {
      q: "Can you set up VLANs and a separate guest network?",
      a: "Yes. We segment smart-home devices, cameras, work laptops, and guests onto separate VLANs so a compromised device or a bandwidth-hungry guest can't reach or slow the rest of the house.",
    },
    {
      q: "Do you monitor the network after install?",
      a: "We offer remote monitoring on the systems we install, so we usually see an ISP outage, an offline access point, or a failing switch before you call us.",
    },
  ],
  security: [
    {
      q: "How much does a home security camera system cost in Denver?",
      a: "A four-camera system with a recorder and professional installation typically starts around $2,800. Most Denver homes land in the $4,000–$12,000 range for eight to sixteen cameras, an NVR, and integrated access control.",
    },
    {
      q: "Do your cameras record locally or to the cloud?",
      a: "Both are available, and we usually recommend local recording to an on-site NVR with optional cloud clips. Local storage means no monthly fee per camera and no lost footage during an internet outage.",
    },
    {
      q: "Can I watch my cameras from my phone?",
      a: "Yes. Every system we install includes secure remote viewing on iOS and Android, and we can surface camera feeds on TVs, in-wall touchscreens, and your automation app.",
    },
    {
      q: "Do the cameras work at night and in Colorado weather?",
      a: "The cameras we install are IP67-rated with infrared or color night vision, rated well below Denver winter lows, and specified with the right lens and mounting for snow glare and long driveways.",
    },
    {
      q: "Can you integrate security with smart locks, lighting, and alarms?",
      a: "Yes. Motion at the driveway can turn on exterior lighting, a doorbell press can pause the theater, and arming the alarm can lock every door and drop the shades. That integration is the whole point of doing it properly.",
    },
    {
      q: "Do you serve all of the Denver Metro Area?",
      a: "We install security and surveillance across Denver, Aurora, Cherry Creek, Highlands Ranch, Castle Rock, Castle Pines, Lone Tree, Parker, Littleton, Lakewood, Boulder, and the rest of the Front Range.",
    },
  ],
  outdoor: [
    {
      q: "How much does an outdoor entertainment system cost in Denver?",
      a: "A patio setup with an outdoor-rated TV, mount, and a pair of speakers typically starts around $4,500 installed. Full outdoor living projects with landscape audio, a large sunlight-readable display, and lighting generally run $10,000–$40,000.",
    },
    {
      q: "Can a regular TV go on a covered Denver patio?",
      a: "We don't recommend it. Indoor TVs fail from temperature swings, moisture, and insects, and they're far too dim in daylight. Outdoor-rated displays are sealed, rated for Colorado temperature extremes, and bright enough to see in shade or full sun.",
    },
    {
      q: "Will outdoor speakers bother my neighbors?",
      a: "Not when they're designed right. We use multiple smaller landscape speakers and buried subwoofers at low volume rather than two loud speakers blasting across the yard — even coverage in your space, much less spill over the fence.",
    },
    {
      q: "Do outdoor systems survive Denver winters?",
      a: "Yes. Everything we install outdoors is weather-rated for freeze-thaw cycles, hail, and UV at altitude. We also seal and drip-loop every penetration so water never tracks back into the house.",
    },
    {
      q: "Can outdoor audio, video, and lighting run from the same app?",
      a: "They can. We tie patio audio, the outdoor TV, landscape lighting, and heaters into the same automation platform and app that runs the inside of the house.",
    },
  ],
  prewire: [
    {
      q: "How much does a new-construction AV prewire cost in Denver?",
      a: "Prewire for a typical Denver custom home runs $6,000–$25,000 depending on square footage and how many zones, cameras, keypads, and TV locations are planned. It is by far the cheapest phase to be generous in — cable is inexpensive while walls are open.",
    },
    {
      q: "When should the low-voltage prewire happen?",
      a: "After rough framing, plumbing, and HVAC, and before insulation and drywall. We coordinate directly with your builder's schedule and walk the house with you at framing to mark every location.",
    },
    {
      q: "What should I prewire even if I'm not installing the equipment yet?",
      a: "At minimum: Cat6 to every TV and access-point location, speaker wire to main living spaces and the patio, conduit to the theater and rack, camera runs at each corner and door, and keypad and shade power at the windows you'll eventually automate. Capped, labeled, and documented for later.",
    },
    {
      q: "Do you work directly with builders and architects?",
      a: "Constantly. We provide low-voltage plans, coordinate with electricians and framers on site, and hand over as-built documentation at completion.",
    },
    {
      q: "What if I skipped the prewire?",
      a: "We retrofit finished homes all the time — fishing cable through existing walls and ceilings with minimal, patchable access. It costs more than prewire, but very little is truly impossible.",
    },
  ],
  retrofit: [
    {
      q: "Can you add wiring to a finished home without major damage?",
      a: "Yes. Retrofit cable fishing is a core specialty. We pull Cat6, speaker wire, and HDMI through existing walls, ceilings, and floor cavities using small, strategically placed access points, then patch, texture, and paint so the wall matches.",
    },
    {
      q: "How much does a retrofit AV install cost in Denver?",
      a: "Single-room retrofits often run $900–$3,500. Whole-home retrofits with audio zones, network, and camera runs typically land between $6,000 and $30,000 depending on the home's construction and access.",
    },
    {
      q: "Do you work in older Denver homes with plaster and lath?",
      a: "Regularly — Wash Park bungalows, Congress Park Victorians, Hilltop and Country Club homes. Plaster, lath, brick, and balloon framing all take different technique, and we plan the access points before we cut anything.",
    },
    {
      q: "Do you patch and paint the drywall yourselves?",
      a: "Yes. Patching, texture matching, and paint are included in the quote on retrofit work. We don't hand you a wall with a hole in it.",
    },
    {
      q: "What if a wire run truly isn't possible?",
      a: "We tell you up front and design around it — surface raceway matched to the trim, a wireless zone amp, or a nearby relocation. No surprise change orders mid-project.",
    },
  ],
  lighting: [
    {
      q: "How much does smart lighting control cost in Denver?",
      a: "A retrofit smart-switch system for a few key rooms typically starts around $2,500 installed. Whole-home centralized lighting control with keypads and engraved scenes generally runs $10,000–$60,000 depending on load count and whether the home is being wired new.",
    },
    {
      q: "Smart switches or centralized lighting control — which should I choose?",
      a: "For existing homes, smart dimmers and keypads replacing the switches you already have deliver most of the benefit with no rewiring. For new construction or a full remodel, centralized panel-based control gives cleaner walls, better scene behavior, and far more reliability.",
    },
    {
      q: "Does smart lighting keep working if the internet goes down?",
      a: "Yes. Every system we install runs locally — switches, keypads, and scenes keep working without internet. Only remote app access and voice control need the connection.",
    },
    {
      q: "Can you add tunable-white or circadian lighting?",
      a: "We do. Tunable fixtures shift from cool, energizing light in the morning to warm, low-blue light at night, on an automatic astronomical schedule tied to Denver sunrise and sunset.",
    },
    {
      q: "Can lighting be tied into shades, audio, and the theater?",
      a: "That's the goal. One button dims the great room, drops the shades, and starts the movie. We program scenes around how you actually use each room.",
    },
  ],
  "smart-home": [
    {
      q: "How much does smart home automation cost in Denver?",
      a: "Focused projects — lighting plus a couple of audio zones and a thermostat — typically start around $6,000. Whole-home automation combining lighting, shades, audio, video, climate, security, and networking generally runs $25,000–$150,000+ depending on home size and finish level.",
    },
    {
      q: "Which automation platform do you recommend?",
      a: "It depends on the home. For fully integrated luxury homes we lean on professionally installed platforms that run locally and handle lighting, shades, audio, video, climate, and security under one interface. For smaller scopes we build around the ecosystem you already live in.",
    },
    {
      q: "Will the system still work if the internet goes out?",
      a: "Yes. Everything we install is designed so the core functions — lights, shades, keypads, scenes, local audio — run on the local network. Remote access and voice assistants need internet; the house does not.",
    },
    {
      q: "Can you automate an existing home, or does it need new wiring?",
      a: "Existing homes are most of our work. We combine retrofit wiring where it's practical with reliable wireless devices where it isn't, so you get one coherent system without gutting the house.",
    },
    {
      q: "Will my family actually be able to use it?",
      a: "That's the real test. We standardize on the same keypad layout and app in every room, label scenes in plain language, and walk your household — and your house sitter — through it before we leave.",
    },
    {
      q: "Do you offer ongoing support?",
      a: "Yes. We provide remote monitoring and support plans on systems we install, so most issues are diagnosed and fixed without a truck roll.",
    },
  ],
  "commercial-av": [
    {
      q: "What does commercial AV installation cost in Denver?",
      a: "A single huddle room or a restaurant zone of background music typically runs $4,500–$12,000. Multi-room office builds, lobby video walls, and church or auditorium systems generally land between $25,000 and $150,000 depending on room count, display size, and how much cabling has to be run through finished space.",
    },
    {
      q: "What kinds of Denver businesses do you work with?",
      a: "Offices and boardrooms downtown and in the Tech Center, restaurants and breweries, gyms and studios, churches and auditoriums, clinics, retail, and multi-tenant buildings that need signage or paging.",
    },
    {
      q: "Do you work with our general contractor and IT team?",
      a: "Yes. We produce signal-flow drawings and rack elevations up front, coordinate conduit and pathway needs with the GC during framing or tenant finish, and hand your IT team documented VLANs, IP schedules, and port maps.",
    },
    {
      q: "Can you take over a system another integrator installed?",
      a: "Often, yes. We start with a site walk to document what's in the racks and what's still supported, then give you a written plan that separates what can be reused from what needs replacing.",
    },
    {
      q: "Do you offer ongoing service agreements?",
      a: "We do. Service-level agreements include remote monitoring, firmware management, and guaranteed on-site response windows so a dead display or microphone doesn't stall a meeting or a service.",
    },
    {
      q: "How long does a commercial AV project take?",
      a: "A single room is usually one to three days on site once material arrives. Multi-room office fit-outs typically run two to six weeks and are scheduled around your business hours, including evening and weekend work when a space can't be down during the day.",
    },
  ],
  conference: [
    {
      q: "How much does a conference room AV installation cost?",
      a: "A single huddle room with a display, camera, speakerphone, and clean cabling typically runs $4,500–$9,000. Larger boardrooms with dual displays, ceiling microphone arrays, DSP, and a control touchpanel generally land between $15,000 and $60,000.",
    },
    {
      q: "Does the system work with Zoom, Teams, and Google Meet?",
      a: "Yes. We build rooms that are certified for the major platforms and can run all of them, so a guest can walk in and present from their own account without calling IT.",
    },
    {
      q: "How do you fix rooms where remote participants can't hear well?",
      a: "Usually with proper microphone coverage and acoustic treatment rather than a louder speaker. Ceiling microphone arrays with beamforming and DSP echo cancellation make a hard-surfaced glass conference room intelligible on the far end.",
    },
    {
      q: "Can people share their screen wirelessly?",
      a: "Yes — wireless presentation with a wired HDMI or USB-C fallback at the table, because the fallback always matters during an important meeting.",
    },
    {
      q: "Do you service commercial spaces across the Denver Metro?",
      a: "We do — offices, clinics, churches, gyms, and restaurants throughout Denver, the Tech Center, Boulder, and the Front Range.",
    },
  ],
  "climate-control": [
    {
      q: "How much does smart climate control cost in Denver?",
      a: "A single smart thermostat integrated into an existing control system typically runs $450–$900 installed. A multi-zone system with wireless temperature sensors, motorized dampers, and automation tied to shades and occupancy generally lands between $3,500 and $12,000 depending on how many zones your HVAC equipment supports.",
    },
    {
      q: "Does smart climate control actually save money on Front Range energy bills?",
      a: "It usually does, because Denver's swing seasons punish fixed schedules. Setbacks based on real occupancy, plus coordinating shades to block afternoon sun on west-facing glass, cut both cooling load in July and heating runtime on cold January mornings. Most homeowners see the biggest savings in rooms that were previously heated or cooled all day for nobody.",
    },
    {
      q: "Will smart climate control work with my existing furnace and AC?",
      a: "In most cases, yes. We check wiring at the equipment, confirm your system's staging and whether it's a heat pump, conventional split system, or boiler, then choose thermostats and zone controllers that match. We coordinate with your HVAC contractor when dampers or new zone wiring are required.",
    },
    {
      q: "Can you control temperature by room instead of by floor?",
      a: "That depends on your ductwork. Where a home already has zone dampers we control each zone directly. Where it doesn't, we place wireless sensors in the rooms that matter — primary bedroom, nursery, home office — and average or prioritize them so the thermostat responds to where you actually are instead of the hallway.",
    },
    {
      q: "Which Denver areas do you cover for climate control installation?",
      a: "The full Denver metro and Front Range, including Denver, Cherry Creek, Aurora, Lakewood, Centennial, Highlands Ranch, Littleton, Parker, Castle Rock, Castle Pines, Golden, Boulder, and Evergreen.",
    },
  ],
  "voice-control": [
    {
      q: "How much does voice control cost to add to a Denver smart home?",
      a: "If you already have a modern control system, adding voice typically runs $350–$1,200 for integration and scene programming. Adding microphone or smart speaker coverage throughout the home, tied into whole-home audio, usually lands between $2,000 and $8,000 depending on room count.",
    },
    {
      q: "Which voice assistants do you integrate with?",
      a: "All the major platforms, so you keep using whichever ecosystem your household already lives in. We map voice commands to real scenes in your control system rather than to loose individual devices, which is why the commands keep working after you add a room or replace a TV.",
    },
    {
      q: "Is voice control a privacy risk?",
      a: "It can be, which is why we offer local processing options for sensitive rooms — commands are interpreted on hardware inside your house rather than sent to a cloud service. We also leave microphones out of bedrooms and offices entirely when a client prefers that, and control those rooms with keypads or app scenes instead.",
    },
    {
      q: "What can I actually control by voice?",
      a: "Lighting and scenes, motorized shades, whole-home and room audio, TV and theater sources, thermostats, door locks, garage doors, and camera views on a display. Compound commands are where it gets useful: 'goodnight' can dim lights, close shades, lock doors, set back the thermostat, and arm the alarm in one phrase.",
    },
    {
      q: "Why does my current voice setup keep failing to hear me?",
      a: "Usually mic placement and wake-word sensitivity. Open Denver floor plans with hard surfaces and a running furnace fan are hostile to a single speaker in the corner. We survey each room, place mics where you actually speak from, and tune sensitivity so it hears you without triggering off the television.",
    },
  ],
  "whole-home-video": [
    {
      q: "How much does whole-home video distribution cost in Denver?",
      a: "A four-TV system with a centralized rack, matrix switching, and unified control typically starts around $6,500 installed. Larger homes with eight or more displays, AV-over-IP switching, and integrated automation generally run $15,000–$45,000. Every project is fixed-bid after an in-home walkthrough.",
    },
    {
      q: "What is the advantage of centralizing cable boxes and streaming devices?",
      a: "Every room gets a clean wall with only the display on it, all sources are shared instead of duplicated, and service and upgrades happen at one rack instead of behind seven TVs. It also removes the clutter of duplicate subscriptions and mismatched remotes.",
    },
    {
      q: "HDBaseT or AV-over-IP — which do you use?",
      a: "It depends on the house. HDBaseT over Category cable is cost-effective and rock-solid for a fixed set of displays. AV-over-IP is the better choice when you want easy expansion, many-to-many switching, or long runs across a large Front Range property, because you add displays by adding endpoints instead of replacing a matrix.",
    },
    {
      q: "Can video distribution be retrofitted into an existing Denver home?",
      a: "Often, yes. It comes down to whether we can get Category cable from a central closet to each TV location. We survey attics, basements, and closet stacks first, and where a run genuinely isn't possible we place a local source or a licensed wireless link at that display instead of guessing.",
    },
    {
      q: "Will everyone in the house be able to watch different things?",
      a: "Yes. A matrix system lets any source appear on any TV independently, so the kids can stream in the basement while a game runs in the great room and the primary suite watches something else. Sports-bar layouts with multiple games across multiple screens are a common request here.",
    },
  ],
};

